import React from 'react';
import './PasswordStrengthIndicator.scss';

interface PasswordStrengthIndicatorProps {
  password: string;
  className?: string;
}

export type PasswordStrength = 'very-weak' | 'weak' | 'medium' | 'strong' | 'very-strong';

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({ 
  password, 
  className = '' 
}) => {
  const calculateStrength = (password: string): PasswordStrength => {
    if (!password) return 'very-weak';
    
    let score = 0;
    
    // Length check
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    
    // Character variety checks
    if (/[a-z]/.test(password)) score += 1; // lowercase
    if (/[A-Z]/.test(password)) score += 1; // uppercase
    if (/[0-9]/.test(password)) score += 1; // numbers
    if (/[^A-Za-z0-9]/.test(password)) score += 1; // special characters
    
    // Deduct points for common patterns
    if (/(.)\1{2,}/.test(password)) score -= 1; // repeated characters
    if (/123|abc|qwe/i.test(password)) score -= 1; // common sequences
    
    // Map score to strength
    if (score <= 1) return 'very-weak';
    if (score <= 2) return 'weak';
    if (score <= 3) return 'medium';
    if (score <= 4) return 'strong';
    return 'very-strong';
  };

  const getStrengthText = (strength: PasswordStrength): string => {
    switch (strength) {
      case 'very-weak': return 'Muy débil';
      case 'weak': return 'Débil';
      case 'medium': return 'Media';
      case 'strong': return 'Fuerte';
      case 'very-strong': return 'Muy fuerte';
      default: return '';
    }
  };

  const getStrengthColor = (strength: PasswordStrength): string => {
    switch (strength) {
      case 'very-weak': return '#ff4444';
      case 'weak': return '#ff8800';
      case 'medium': return '#ffbb33';
      case 'strong': return '#00C851';
      case 'very-strong': return '#007E33';
      default: return '#ccc';
    }
  };

  const getStrengthWidth = (strength: PasswordStrength): string => {
    switch (strength) {
      case 'very-weak': return '20%';
      case 'weak': return '40%';
      case 'medium': return '60%';
      case 'strong': return '80%';
      case 'very-strong': return '100%';
      default: return '0%';
    }
  };

  const strength = calculateStrength(password);
  const strengthText = getStrengthText(strength);
  const strengthColor = getStrengthColor(strength);
  const strengthWidth = getStrengthWidth(strength);

  if (!password) return null;

  return (
    <div className={`password-strength ${className}`}>
      <div className="password-strength__bar">
        <div 
          className="password-strength__progress"
          style={{ 
            width: strengthWidth,
            backgroundColor: strengthColor
          }}
        />
      </div>
      <div className="password-strength__text" style={{ color: strengthColor }}>
        {strengthText}
      </div>
    </div>
  );
};

export default PasswordStrengthIndicator;
